import React, { useState } from 'react';
import { Mail, Phone, Linkedin, ShieldCheck, FileCheck, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalModal from './LegalModal';
import KitDigitalBanner from './KitDigitalBanner';
import useLang from '../hooks/useLang';
import { showCookiePreferences } from '../lib/cookieConsent';
import { APP_VERSION } from '../version';
import { toast } from 'sonner';
// Componente SVG Vectorial Premium para el CCN-CNI (100% HD e independiente de red)
const SvgCcnLogo = () => (
  <svg viewBox="0 0 100 100" className="h-16 sm:h-20 w-auto object-contain rounded border border-white/10 shadow-lg bg-white p-1.5 transition-transform duration-300 group-hover:scale-105" aria-hidden="true">
    <circle cx="50" cy="50" r="45" fill="#0A1833" />
    <circle cx="50" cy="50" r="41" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
    <circle cx="50" cy="50" r="38" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
    <rect x="38" y="46" width="24" height="18" rx="2" fill="#D4AF37" />
    <path d="M43 46V38c0-3.8 3.1-7 7-7s7 3.2 7 7v8" fill="none" stroke="#D4AF37" strokeWidth="3.5" />
    <circle cx="50" cy="54" r="2.5" fill="#0A1833" />
    <path d="M50 56.5v4" stroke="#0A1833" strokeWidth="1.5" strokeLinecap="round" />
    <path id="textPathCcn" d="M 18,50 A 32,32 0 1,1 82,50" fill="none" />
    <text fill="#ffffff" fontSize="7.5" fontWeight="bold" fontFamily="monospace" letterSpacing="1.2">
      <textPath href="#textPathCcn" startOffset="50%" textAnchor="middle">
        CCN-CNI
      </textPath>
    </text>
  </svg>
);

// Componente SVG Vectorial Premium para la AEPD (100% HD e independiente de red)
const SvgAepdLogo = () => (
  <svg viewBox="0 0 100 100" className="h-16 sm:h-20 w-auto object-contain rounded border border-white/10 shadow-lg bg-white p-1.5 transition-transform duration-300 group-hover:scale-105" aria-hidden="true">
    <circle cx="50" cy="50" r="45" fill="#FFFFFF" />
    <circle cx="50" cy="50" r="42" fill="none" stroke="#005691" strokeWidth="2.5" />
    <path d="M50 20 L75 32 V55 C75 67 65 77 50 82 C35 77 25 67 25 55 V32 Z" fill="#005691" />
    <path d="M50 25 L70 35 V54 C70 64 61 72 50 76 C39 72 30 64 30 54 V35 Z" fill="none" stroke="#F39200" strokeWidth="2" />
    <circle cx="50" cy="46" r="6" fill="#F39200" />
    <path d="M50 49.5v12 M44 57h12" stroke="#F39200" strokeWidth="2" strokeLinecap="round" />
    <text x="50" y="69" fill="#FFFFFF" fontSize="9.5" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">
      AEPD
    </text>
  </svg>
);

// Componente SVG Vectorial Premium para el INCIBE-CERT (100% HD e independiente de red)
const SvgIncibeLogo = () => (
  <svg viewBox="0 0 100 100" className="h-16 sm:h-20 w-auto object-contain rounded border border-white/10 shadow-lg bg-white p-1.5 transition-transform duration-300 group-hover:scale-105" aria-hidden="true">
    <circle cx="50" cy="50" r="45" fill="#0C1B40" />
    {/* Arco dinámico azul y naranja */}
    <circle cx="50" cy="50" r="42" fill="none" stroke="#2563EB" strokeWidth="1.5" strokeDasharray="3 2" />
    <circle cx="50" cy="50" r="39" fill="none" stroke="#FF7A00" strokeWidth="1.5" />
    
    {/* Isotipo candado y escudo */}
    <path d="M43 45 c0-6 14-6 14 0 v3 h-14 z" stroke="#FF7A00" strokeWidth="2" strokeLinecap="round" fill="none" />
    <rect x="39" y="48" width="22" height="15" rx="2" fill="#0A1833" stroke="#FF7A00" strokeWidth="2" />
    <circle cx="50" cy="54" r="2" fill="#FF7A00" />
    <path d="M50 56v4" stroke="#FF7A00" strokeWidth="1.5" strokeLinecap="round" />

    <text x="50" y="34" fill="#FFFFFF" fontSize="13" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="0.2">incibe</text>
    <rect x="35" y="38" width="30" height="1" fill="#FF7A00" />
    <text x="50" y="76" fill="#FF7A00" fontSize="7.5" fontWeight="950" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1.5">CERT</text>
  </svg>
);

const translations = {
  es: {
    tagline: 'Auditoría Forense | Cumplimiento Normativo | Certificación ENS',
    desc: 'Auditoría forense de ciberseguridad y cumplimiento normativo con validez pericial. Metodología Full-Stack Compliance Audit UE 2026.',
    linksTitle: 'Enlaces Rápidos',
    contactTitle: 'Contacto',
    location: 'España',
    
    // Quick links
    link_home: 'Inicio',
    link_method: 'Metodología',
    link_cases: 'Casos Exitosos',
    link_about: 'Sobre mí',

    link_contact: 'Contacto',
    link_adca: 'ADCA Temporal (SaaS)',
    link_self_healing: 'Ciberseguridad Autónoma (SOC)',
    link_eslabon: 'Test Eslabón Débil NIS2',


    
    // Bottom Seals
    seal1: 'Formación específica en el ENS (CCN)',
    seal2: 'Informes con validez judicial (Art. 335 LEC)',
    seal3: 'Metodología basada en guías CCN-STIC',
    
    // Copyright
    copyright: 'Joaquín Burgos Bayo - Auditorialegal. Todos los derechos reservados.',
    
    // Legal links
    legal1: 'Aviso Legal',
    legal2: 'Política de Privacidad',
    legal_redes: 'Política de Redes Sociales',
    legal3: 'Política de Cookies',
    legal_sales: 'Condiciones de Compra',
    legal_terms: 'Términos y Condiciones',
    legal4: 'Widget de Accesibilidad',
    legal_accessibility_declaration: 'Declaración de Accesibilidad (RD 1112/2018)',
    legal_whistleblowing: 'Canal de Denuncias (Ley 2/2023)',
    legal_security_notice: 'Aviso de Seguridad',
    legal_technical_requirements: 'Requisitos Técnicos',
    legal_quality: 'Política de Calidad y Medioambiente Digital',
    legal_acoso: 'Protocolo Acoso Laboral',
    legal_ia: 'Gobernanza de IA (AI Act)',
    legal_desconexion: 'Desconexión Digital',
    legal_psi: 'Política de Seguridad de la Información (PSI - ENS)',
    legal_igualdad: 'Política de Igualdad de Trato (Ley Zerolo)',
    legal_proveedores: 'Seguridad de la Cadena de Suministro (NIS2)',
    legal_data_act: 'Gobernanza e Intercambio de Datos (Data Act)',
    level1_title: 'NIVEL 1: CUMPLIMIENTO ESENCIAL',
    level1_desc: 'Autónomos y Micro-Pymes',
    level2_title: 'NIVEL 2: CUMPLIMIENTO AVANZADO',
    level2_desc: 'Pymes y Empresas con Personal',
    level3_title: 'NIVEL 3: EXCELENCIA & ADMINISTRACIONES',
    level3_desc: 'Corporaciones, NIS2 e Infraestructuras',
    compliance_progression_title: 'Ecosistema de Cumplimiento Normativo por Niveles de Exigencia',
    complianceEcosystem: 'Este sitio web incorpora un ecosistema integral de cumplimiento y ciberseguridad: Analizador Web en tiempo real, Widget de Accesibilidad (RD 1112/2018), Gestión de Cookies inteligente (AEPD), Políticas y Aviso Legal dinámicos, Canal de Denuncias (Ley 2/2023), y Asistente de IA. Diseñado y acreditado como Powered by Auditorialegal.',
    disclaimer1: 'ISO 27001, NIS2 y DORA son marcas, estándares y directivas de ciberseguridad internacionales/UE. Auditorialegal.es es una plataforma independiente de consultoría pericial tecnológica y automatización de cumplimiento (GRC), no estando afiliada de forma oficial a la ISO ni a organismos reguladores de la Unión Europea.',
    disclaimer2: 'Los diagnósticos preliminares y pre-análisis OSINT se basan en técnicas de reconocimiento pasivo y análisis de superficies de ataque externas expuestas públicamente. No constituyen una auditoría técnica intrusiva interna de penetración de sistemas (pentesting).',
    disclaimer3: 'TISAX® es una marca registrada de la ENX Association. Auditorialegal no está afiliada a la ENX Association y ofrece servicios de consultoría técnica preparatoria únicamente. PCI DSS es una marca registrada de PCI Security Standards Council.'
  },
  en: {
    tagline: 'Forensic Audit | Regulatory Compliance | ENS Certification',
    desc: 'Cybersecurity forensic audit and regulatory compliance with expert witness validity. Full-Stack Compliance Audit EU 2026 methodology.',
    linksTitle: 'Quick Links',
    contactTitle: 'Contact',
    location: 'Spain',
    
    // Quick links
    link_home: 'Home',
    link_method: 'Methodology',
    link_cases: 'Success Stories',
    link_about: 'About me',
    link_contact: 'Contact',
    link_adca: 'ADCA Temporal (SaaS)',
    link_self_healing: 'Autonomous Security (SOC)',
    link_eslabon: 'Weak Link Test NIS2',


    
    // Bottom Seals
    seal1: 'Specific ENS Training (CCN)',
    seal2: 'Reports with expert legal validity (Art. 335 LEC)',
    seal3: 'Methodology based on CCN-STIC guidelines',
    
    // Copyright
    copyright: 'Joaquín Burgos Bayo - Auditorialegal. All rights reserved.',
    
    // Legal links
    legal1: 'Legal Notice',
    legal2: 'Privacy Policy',
    legal_redes: 'Social Media Policy',
    legal3: 'Cookie Policy',
    legal_sales: 'Terms of Purchase',
    legal_terms: 'Terms of Service',
    legal4: 'Accessibility Widget',
    legal_accessibility_declaration: 'Accessibility Declaration (RD 1112/2018)',
    legal_whistleblowing: 'Whistleblowing Channel (Law 2/2023)',
    legal_security_notice: 'Security Notice',
    legal_technical_requirements: 'Technical Requirements',
    legal_quality: 'Quality & Environmental Digital Policy',
    legal_acoso: 'Harassment Prevention Protocol',
    legal_ia: 'AI Governance (AI Act)',
    legal_desconexion: 'Digital Disconnection',
    legal_psi: 'Information Security Policy (ISP - ENS)',
    legal_igualdad: 'Equal Treatment Policy (Zerolo Law)',
    legal_proveedores: 'Supply Chain Security (NIS2)',
    legal_data_act: 'Data Governance & Sharing (Data Act)',
    level1_title: 'LEVEL 1: ESSENTIAL COMPLIANCE',
    level1_desc: 'Freelancers and Micro-SMEs',
    level2_title: 'LEVEL 2: ADVANCED COMPLIANCE',
    level2_desc: 'SMEs and Companies with Staff',
    level3_title: 'LEVEL 3: EXCELLENCE & PUBLIC SECTOR',
    level3_desc: 'Corporations, NIS2 & Infrastructures',
    compliance_progression_title: 'Regulatory Compliance Ecosystem by Exigency Levels',
    complianceEcosystem: 'This website incorporates a comprehensive compliance and cybersecurity ecosystem: Real-time Web Analyzer, Accessibility Widget (RD 1112/2018), Intelligent Cookie Consent (AEPD), Dynamic Privacy & Legal Policies, Whistleblowing Channel (Law 2/2023), and AI Assistant. Designed and certified as Powered by Auditorialegal.',
    disclaimer1: 'ISO 27001, NIS2, and DORA are international/EU cybersecurity standards, marks, and directives. Auditorialegal.es is an independent platform for technical forensic consulting and compliance automation (GRC), not officially affiliated with ISO or European Union regulatory bodies.',
    disclaimer2: 'Preliminary assessments and OSINT pre-analyses are based on passive recognition techniques and public-facing attack surface analysis. They do not constitute an intrusive internal security audit or network penetration test (pentesting).',
    disclaimer3: 'TISAX® is a registered trademark of the ENX Association. Auditorialegal is not affiliated with the ENX Association and offers preparatory technical consulting services only. PCI DSS is a registered trademark of the PCI Security Standards Council.'
  }
};

export const Footer = () => {
  const lang = useLang();
  const t = translations[lang];
  const [modalType, setModalType] = useState(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openLegalModal = (type) => (e) => {
    e.preventDefault();
    setModalType(type);
  };

  return (
    <footer role="contentinfo" className="bg-primary text-primary-foreground">
      <KitDigitalBanner />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-4">
              <img
                src="/logo-badge.png"
                alt="Auditorialegal - LegalSec Consulting"
                width={48}
                height={48}
                loading="lazy"
                decoding="async"
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <span className="text-2xl font-serif font-bold block">Auditorialegal</span>
                <span className="text-xs text-primary-foreground/80">
                  {t.tagline}
                </span>
              </div>
            </Link>
            <p className="text-primary-foreground/80 leading-relaxed max-w-md mb-4">
              {t.desc}
            </p>
            <div className="flex items-center gap-4">
              <a
                href="mailto:info@auditorialegal.es"
                className="text-primary-foreground/80 hover:text-accent no-transform-transition"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a
                href="tel:+34633115067"
                className="text-primary-foreground/80 hover:text-accent no-transform-transition"
                aria-label="Phone"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/joaqu%C3%ADn-burgos-auditorialegal/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-foreground/80 hover:text-accent no-transform-transition"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t.linksTitle}</h3>
            <ul className="space-y-2">
              {[
                { label: t.link_home, id: 'hero' },
                { label: t.link_method, id: 'metodologia' },
                { label: t.link_cases, id: 'casos-exito' },
                { label: t.link_about, id: 'sobre-mi' },
                { label: t.link_contact, id: 'contacto' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-primary-foreground/80 hover:text-accent no-transform-transition text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/adca-temporal"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition text-sm block"
                >
                  {t.link_adca}
                </Link>
              </li>
              <li>
                <Link
                  to="/self-healing"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition text-sm block"
                >
                  {t.link_self_healing}
                </Link>
              </li>
              <li>
                <Link
                  to="/simulador-eslabon-debil"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition text-sm block font-bold text-accent"
                >
                  ✨ {t.link_eslabon}
                </Link>
              </li>
              <li>
                <Link
                  to="/canal-denuncias"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition text-sm block font-bold text-rose-400"
                >
                  🔒 {lang === 'es' ? 'Canal Ético (Ley 2/2023)' : 'Ethical Whistleblowing (Law 2/2023)'}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openLegalModal('trabaja-con-nosotros')}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 hover:border-orange-500/60 text-orange-400 hover:text-orange-300 transition-all duration-300 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-orange-500/50 mt-1.5 shadow-[0_2px_8px_rgba(249,115,22,0.08)] hover:shadow-[0_2px_12px_rgba(249,115,22,0.18)]"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span>{lang === 'es' ? 'Trabaja con nosotros' : 'Work with us'}</span>
                  <span className="text-[9px] uppercase bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-sm tracking-wide ml-0.5">
                    {lang === 'es' ? '¡Únete!' : 'Hiring'}
                  </span>
                </button>
              </li>

            </ul>
          </div>

          {/* Marcos & Gobernanza (Inspirado en Frameworks de DataGuard) */}
          <div>
            <h3 className="font-bold text-lg mb-4">{lang === 'es' ? 'Marcos & Gobernanza' : 'Frameworks & Governance'}</h3>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li>
                <button
                  onClick={() => scrollToSection('metodologia')}
                  className="hover:text-accent transition-colors text-left block"
                >
                  🛡️ ENS (Esquema Nacional)
                </button>
              </li>
              <li>
                <button
                  onClick={openLegalModal('seguridad-proveedores')}
                  className="hover:text-accent transition-colors text-left block"
                >
                  🔗 Directiva NIS2
                </button>
              </li>
              <li>
                <button
                  onClick={openLegalModal('politica-ia')}
                  className="hover:text-accent transition-colors text-left block"
                >
                  🤖 Gobernanza IA (AI Act)
                </button>
              </li>
              <li>
                <button
                  onClick={openLegalModal('canal-denuncias')}
                  className="hover:text-accent transition-colors text-left block font-semibold text-accent/90"
                >
                  🔒 Ley 2/2023 (Denuncias)
                </button>
              </li>
              <li>
                <button
                  onClick={openLegalModal('politica-psi')}
                  className="hover:text-accent transition-colors text-left block"
                >
                  📖 ISO 27001 / PSI
                </button>
              </li>
              <li>
                <button
                  onClick={openLegalModal('data-act')}
                  className="hover:text-accent transition-colors text-left block"
                >
                  📊 Reglamento Data Act
                </button>
              </li>
              <li>
                <Link
                  to="/productos"
                  className="hover:text-accent transition-colors text-left block"
                >
                  ⚙️ {lang === 'es' ? 'Ley de Ciberresiliencia (CRA)' : 'Cyber Resilience Act (CRA)'}
                </Link>
              </li>
              <li>
                <Link
                  to="/productos"
                  className="hover:text-accent transition-colors text-left block"
                >
                  🌐 {lang === 'es' ? 'Esquema Interoperabilidad (ENI)' : 'Interoperability Scheme (ENI)'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t.contactTitle}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="mailto:info@auditorialegal.es"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition"
                >
                  info@auditorialegal.es
                </a>
              </li>
              <li>
                <a
                  href="tel:+34633115067"
                  className="text-primary-foreground/80 hover:text-accent no-transform-transition"
                >
                  +34 633 115 067
                </a>
              </li>
              <li className="text-primary-foreground/80">{t.location}</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/10 pt-8">
          {/* Trust Seals & Acreditaciones en Imagen - Estilo Premium Glassmorphism */}
          <div className="flex flex-col items-center gap-6 mb-8 pb-8 border-b border-primary-foreground/10 bg-white/[0.02] border border-white/5 rounded-2xl p-6 backdrop-blur-[2px] shadow-inner">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold text-center">
              {lang === 'es' ? 'Certificaciones Oficiales y Entidades Colaboradoras' : 'Official Certifications & Collaborating Entities'}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
              {/* Certificado ENS */}
              <div className="flex flex-col items-center gap-2 group">
                <a
                  href="/certificado-ens.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent rounded"
                aria-label={lang === 'es' ? 'Ver Acreditación de formación en el ENS de Joaquín Burgos Bayo' : 'View Official ENS training Accreditation of Joaquín Burgos Bayo'}
                >
                  <img
                    src="/certificado-ens.png"
                    alt="Acreditación ENS"
                    width={80}
                    height={80}
                    className="h-16 sm:h-20 w-auto object-contain rounded border border-white/10 shadow-lg bg-white p-1.5 transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </a>
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1">Acreditación ENS</span>
              </div>

              {/* Sello ENS */}
              <div className="flex flex-col items-center gap-2 group">
                <img
                  src="/sello-ens.png"
                  alt="Sello de Conformidad ENS"
                  width={80}
                  height={80}
                  className="h-16 sm:h-20 w-auto object-contain rounded border border-white/10 shadow-lg bg-white p-1.5 transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1">Sello ENS</span>
              </div>

              {/* Sello Colaborador / Auditoria */}
              <button
                onClick={openLegalModal('certificado-seguridad')}
                className="flex flex-col items-center gap-2 group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg p-1 transition-all"
              >
                <img
                  src="/logo-badge.png"
                  alt="Sello de Auditorías Internas Acreditadas"
                  width={80}
                  height={80}
                  className="h-16 sm:h-20 w-auto object-contain rounded-full border border-white/10 shadow-lg bg-[#0A0E17] p-1.5 transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1 group-hover:text-accent transition-colors">Auditoría Pericial</span>
              </button>

              {/* Organismo Oficial: CCN */}
              <div className="flex flex-col items-center gap-2 group">
                <a
                  href="/acreditacion-ccn.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-95 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent rounded"
                  aria-label={lang === 'es' ? 'Acreditación CCN - Ver formación de Joaquín Burgos Bayo' : 'CCN Accreditation - View training of Joaquín Burgos Bayo'}
                >
                  <SvgCcnLogo />
                </a>
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1">Acreditación CCN</span>
              </div>

              {/* Organismo Oficial: AEPD */}
              <div className="flex flex-col items-center gap-2 group">
                <a
                  href="https://www.aepd.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-95 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent rounded"
                  aria-label={lang === 'es' ? 'AEPD - Visitar web oficial de la Agencia Española de Protección de Datos' : 'AEPD - Visit official website of the Spanish Data Protection Agency'}
                >
                  <SvgAepdLogo />
                </a>
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1">AEPD</span>
              </div>

              {/* Organismo Oficial: INCIBE-CERT */}
              <div className="flex flex-col items-center gap-2 group">
                <a
                  href="https://www.incibe.es/incibe-cert"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-95 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent rounded"
                  aria-label={lang === 'es' ? 'INCIBE-CERT - Visitar web oficial del Instituto Nacional de Ciberseguridad' : 'INCIBE-CERT - Visit official website of the National Institute of Cybersecurity'}
                >
                  <SvgIncibeLogo />
                </a>
                <span className="text-[10px] text-primary-foreground/65 font-mono mt-1">INCIBE-CERT</span>
              </div>
            </div>
          </div>

          {/* Trust Seals */}
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <a
              href="/acreditacion-ccn.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-primary-foreground/80 hover:text-accent transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-accent rounded p-1 group/seal"
              aria-label={lang === 'es' ? `${t.seal1} - Ver Acreditación oficial del CCN` : `${t.seal1} - View official CCN Accreditation`}
            >
              <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 transition-transform group-hover/seal:scale-110" />
              <span className="underline decoration-dotted decoration-accent/40 hover:decoration-accent">{t.seal1}</span>
            </a>
            <div className="flex items-center justify-center gap-2 text-sm text-primary-foreground/80">
              <Scale className="w-5 h-5 text-accent flex-shrink-0" />
              <span>{t.seal2}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-primary-foreground/80">
              <FileCheck className="w-5 h-5 text-accent flex-shrink-0" />
              <span>{t.seal3}</span>
            </div>
          </div>

          {/* Ecosistema de Cumplimiento Normativo por Niveles */}
          <div className="mt-12 pt-10 border-t border-primary-foreground/10">
            <h4 className="text-sm font-serif font-bold text-accent uppercase tracking-widest text-center mb-8">
              {t.compliance_progression_title}
            </h4>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Columna 1: Cumplimiento Esencial */}
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 backdrop-blur-sm shadow-inner transition-all hover:bg-white/[0.04] hover:border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-3">
                    <h5 className="text-xs font-bold tracking-wider text-slate-100 uppercase">{t.level1_title}</h5>
                    <span className="text-[9px] bg-blue-500/10 border border-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
                      {t.level1_desc}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2.5 items-start text-xs text-primary-foreground/70">
                    <button
                      type="button"
                      onClick={openLegalModal('aviso-legal')}
                      data-testid="footer-link-aviso-legal"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal1}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('terminos-condiciones')}
                      data-testid="footer-link-terminos-condiciones"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_terms}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('politica-privacidad')}
                      data-testid="footer-link-politica-privacidad"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal2}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('politica-cookies')}
                      data-testid="footer-link-politica-cookies"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal3}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('politica-ventas')}
                      data-testid="footer-link-politica-ventas"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_sales}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('politica-redes')}
                      data-testid="footer-link-politica-redes"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_redes}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('declaracion-accesibilidad')}
                      data-testid="footer-link-declaracion-accesibilidad"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_accessibility_declaration}
                    </button>
                  </div>
                </div>
              </div>

              {/* Columna 2: Cumplimiento Avanzado */}
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 backdrop-blur-sm shadow-inner transition-all hover:bg-white/[0.04] hover:border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-3">
                    <h5 className="text-xs font-bold tracking-wider text-slate-100 uppercase">{t.level2_title}</h5>
                    <span className="text-[9px] bg-amber-500/10 border border-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
                      {t.level2_desc}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2.5 items-start text-xs text-primary-foreground/70">
                    <button
                      type="button"
                      onClick={openLegalModal('canal-denuncias')}
                      data-testid="footer-link-canal-denuncias"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_whistleblowing}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('protocolo-acoso')}
                      data-testid="footer-link-protocolo-acoso"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_acoso}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('desconexion-digital')}
                      data-testid="footer-link-desconexion-digital"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_desconexion}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('ley-zerolo')}
                      data-testid="footer-link-ley-zerolo"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/btn:bg-accent transition-colors animate-pulse" />
                      <span className="font-semibold text-accent/90">{t.legal_igualdad}</span>
                    </button>
                    <Link
                      to="/politica-calidad"
                      data-testid="footer-link-politica-calidad"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_quality}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Columna 3: Cumplimiento de Alta Gama */}
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 backdrop-blur-sm shadow-inner transition-all hover:bg-white/[0.04] hover:border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-3">
                    <h5 className="text-xs font-bold tracking-wider text-slate-100 uppercase">{t.level3_title}</h5>
                    <span className="text-[9px] bg-rose-500/10 border border-rose-500/20 text-rose-300 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
                      {t.level3_desc}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2.5 items-start text-xs text-primary-foreground/70">
                    <button
                      type="button"
                      onClick={openLegalModal('politica-psi')}
                      data-testid="footer-link-politica-psi"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors animate-pulse" />
                      <span className="font-semibold text-accent/90">{t.legal_psi}</span>
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('seguridad-proveedores')}
                      data-testid="footer-link-seguridad-proveedores"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors animate-pulse" />
                      <span className="font-semibold text-accent/90">{t.legal_proveedores}</span>
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('requisitos-tecnicos')}
                      data-testid="footer-link-requisitos-tecnicos"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_technical_requirements}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('politica-ia')}
                      data-testid="footer-link-politica-ia"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_ia}
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('data-act')}
                      data-testid="footer-link-politica-data-act"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors animate-pulse" />
                      <span className="font-semibold text-accent/90">{t.legal_data_act}</span>
                    </button>
                    <button
                      type="button"
                      onClick={openLegalModal('aviso-seguridad')}
                      data-testid="footer-link-aviso-seguridad"
                      className="hover:text-accent transition-colors text-left flex items-center gap-2 group/btn"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 group-hover/btn:bg-accent transition-colors" />
                      {t.legal_security_notice}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Fila de cookies, accesibilidad, secops y trust center */}
            <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs text-primary-foreground/60">
              <button
                type="button"
                onClick={openLegalModal('trust-center')}
                className="hover:text-accent transition-colors flex items-center gap-1.5 font-bold text-accent/90 bg-accent/5 border border-accent/10 hover:border-accent/30 px-3 py-1 rounded-full transition-all duration-300"
                aria-label={lang === 'es' ? 'Centro de Transparencia (Trust Center) - Ver portal de transparencia' : 'Transparency Portal (Trust Center) - View transparency portal'}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span>{lang === 'es' ? 'Centro de Transparencia (Trust Center)' : 'Transparency Portal (Trust Center)'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const trigger = document.getElementById('al-acc-trigger');
                  const container = document.getElementById('auditorialegal-accessibility-widget');
                  if (container) {
                    container.classList.remove('accessibility-dismissed');
                    localStorage.removeItem('al-accessibility-dismissed');
                  }
                  if (trigger) {
                    trigger.click();
                  } else {
                    const host = document.getElementById('snn-accessibility-widget-container');
                    if (host) {
                      const btn = host.shadowRoot?.getElementById('snn-accessibility-button');
                      if (btn) btn.click();
                    }
                  }
                }}
                data-testid="footer-accessibility-link"
                className="hover:text-accent transition-colors flex items-center gap-1.5"
                aria-label={lang === 'es' ? `${t.legal4} - Abrir panel de accesibilidad` : `${t.legal4} - Open accessibility panel`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {t.legal4}
              </button>
              
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('show-cookie-button'));
                  setTimeout(() => {
                    showCookiePreferences();
                  }, 50);
                }}
                className="hover:text-accent transition-colors flex items-center gap-1.5"
                aria-label={lang === 'es' ? 'Preferencias de Cookies - Abrir configuración de cookies' : 'Cookie Preferences - Open cookie settings'}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {lang === 'es' ? 'Preferencias de Cookies' : 'Cookie Preferences'}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('show-secops-monitor'));
                }}
                className="hover:text-accent transition-colors flex items-center gap-1.5"
                aria-label={lang === 'es' ? 'Monitor de Seguridad SecOps - Activar monitor de seguridad' : 'SecOps Security Monitor - Activate security monitor'}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {lang === 'es' ? 'Monitor de Seguridad SecOps' : 'SecOps Security Monitor'}
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 mt-8 border-t border-primary-foreground/10 text-xs text-primary-foreground/60">
            <div className="flex flex-col gap-1 items-center sm:items-start">
              <p>
                © {new Date().getFullYear()} {t.copyright}
              </p>
              <p className="text-[10px] text-slate-400">
                Versión {APP_VERSION} · Auditorialegal Full-Stack Compliance Ecosystem
              </p>
            </div>

            {/* Selector de Idioma Dinámico Premium estilo DataGuard */}
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/5 px-3 py-1.5 rounded-full backdrop-blur-sm shadow-sm">
              <span className="text-[11px] text-primary-foreground/85 uppercase tracking-wider font-semibold">
                {lang === 'es' ? 'Idioma:' : 'Language:'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (lang !== 'es') {
                      window.dispatchEvent(new CustomEvent('languageChange', { detail: 'es' }));
                      localStorage.setItem('lang', 'es');
                      toast.success('Idioma cambiado a Español');
                    }
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-all ${
                    lang === 'es'
                      ? 'bg-accent text-accent-foreground shadow-gold'
                      : 'text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/5'
                  }`}
                  aria-label="Cambiar idioma a Español"
                >
                  ES
                </button>
                <span className="text-white/10 select-none">|</span>
                <button
                  onClick={() => {
                    if (lang !== 'en') {
                      window.dispatchEvent(new CustomEvent('languageChange', { detail: 'en' }));
                      localStorage.setItem('lang', 'en');
                      toast.success('Language changed to English');
                    }
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-all ${
                    lang === 'en'
                      ? 'bg-accent text-accent-foreground shadow-gold'
                      : 'text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/5'
                  }`}
                  aria-label="Change language to English"
                >
                  EN
                </button>
              </div>
            </div>
          </div>

          {/* Ecosistema de Cumplimiento / Powered by Auditorialegal */}
          <div className="mt-6 pt-6 border-t border-primary-foreground/5 text-center">
            <p className="text-[11px] sm:text-xs text-primary-foreground/60 leading-relaxed max-w-4xl mx-auto">
              {t.complianceEcosystem.split('Powered by Auditorialegal')[0]}
              <span className="text-accent font-semibold tracking-wider font-mono">Powered by Auditorialegal</span>
              {t.complianceEcosystem.split('Powered by Auditorialegal')[1] || ''}
            </p>
          </div>

          {/* Notas del Sistema / Disclaimers Premium (Inspiradas en DataGuard) */}
          <div className="mt-6 pt-4 border-t border-primary-foreground/5 text-left text-[9px] text-slate-400 leading-relaxed space-y-2 max-w-4xl mx-auto">
            <p className="flex gap-1.5 items-start">
              <span className="text-[10px] select-none">ℹ</span>
              <span>{t.disclaimer1}</span>
            </p>
            <p className="flex gap-1.5 items-start">
              <span className="text-[10px] select-none">ℹ</span>
              <span>{t.disclaimer2}</span>
            </p>
            <p className="flex gap-1.5 items-start">
              <span className="text-[10px] select-none">ℹ</span>
              <span>{t.disclaimer3}</span>
            </p>
          </div>

        </div>
      </div>

      <LegalModal
        open={!!modalType}
        type={modalType || 'aviso-legal'}
        onOpenChange={(open) => !open && setModalType(null)}
      />
    </footer>
  );
};

export default Footer;
